/* eslint-disable max-len */
/**
 * @license
 * Copyright 2019 Google LLC. All Rights Reserved.
 */

import * as facetfjs from "@tensorflow-models/face-tfjs";
import * as tf from "@tensorflow/tfjs-core";
import * as tfjsWasm from "@tensorflow/tfjs-backend-wasm";
import "@tensorflow/tfjs-backend-webgl";
import "@tensorflow/tfjs-backend-cpu";
// import { math } from '@tensorflow/tfjs-core';

// tfjsWasm.setWasmPaths(`${window.location.origin}/`);
tfjsWasm.setWasmPaths(
  `https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-backend-wasm@${tfjsWasm.version_wasm}/dist/`
);

const stats = new Stats();
stats.showPanel(0);
document.getElementById("stats").appendChild(stats.domElement);
// document.body.prepend(stats.domElement);

let modelFace;
let modelEmotion;
let modelMask;
let ctxOutput;
// let webcamWidth;
// let webcamHeight;
let webcam;
let canvas;
let valueNeutral;
let valueHappy;
let valueSad;
let valueAngry;
let valueSuprised;
let valuePositive;
let valueActive;
let valueVibe;
// let imgWidth;
// let imgHeight;
let viewerType;
let img;
let videoClip;
// let imgVideo;
let canvasV;

let dataEmotionsNeutral = [];
let dataEmotionsAngry = [];
let dataEmotionsHappy = [];
let dataEmotionsSurprised = [];
let dataEmotionsSad = [];
let dataEmotionsPositive = [];
let dataEmotionsActive = [];
let dataEmotionsVibe = [];

let numberCells;
let numberCell;
let myChart;
// eslint-disable-next-line one-var
let beginTime = Date.now(),
  prevTime = beginTime;
const maxFrameChart = 50;
const timeFrameCapture = 300;
const tensionValue = 0.4;

const colorAngry = "rgba(255, 77, 79, 1)";
const colorNeutral = "rgba(186, 231, 255, 1)";

const datasetAngry = {
  label: "ANGRY",
  data: dataEmotionsAngry,
  fill: false,
  tension: tensionValue,
  backgroundColor: [colorAngry],
  borderColor: [colorAngry],
  borderWidth: 2,
};
const datasetNeutral = {
  label: "NEUTRAL",
  data: dataEmotionsNeutral,
  fill: false,
  tension: tensionValue,
  backgroundColor: [colorNeutral],
  borderColor: [colorNeutral],
  borderWidth: 2,
};
const datasetHappy = {
  label: "HAPPY",
  data: dataEmotionsHappy,
  fill: false,
  tension: tensionValue,
  backgroundColor: ["rgba(24, 144, 255, 1)"],
  borderColor: ["rgba(24, 144, 255, 1)"],
  borderWidth: 2,
};
const datasetSad = {
  label: "SAD",
  data: dataEmotionsSad,
  fill: false,
  tension: tensionValue,
  backgroundColor: ["#10239E"],
  borderColor: ["#10239E"],
  borderWidth: 2,
};
const datasetSurprised = {
  label: "SURPRISE",
  data: dataEmotionsSurprised,
  fill: false,
  tension: tensionValue,
  backgroundColor: ["rgba(255, 197, 61, 1)"],
  borderColor: ["rgba(255, 197, 61, 1)"],
  borderWidth: 2,
};

const datasetPositive = {
  label: "POSITIVE",
  data: dataEmotionsPositive,
  fill: false,
  tension: tensionValue,
  backgroundColor: ["rgba(67, 67, 67, 1)"],
  borderColor: ["rgba(67, 67, 61, 1)"],
  borderWidth: 2,
};
const datasetActive = {
  label: "ACTIVE",
  data: dataEmotionsActive,
  fill: false,
  tension: tensionValue,
  backgroundColor: ["rgba(115, 209, 61, 1)"],
  borderColor: ["rgba(115, 209, 61, 1)"],
  borderWidth: 2,
};
const datasetVibe = {
  label: "VIBE",
  data: dataEmotionsVibe,
  fill: false,
  tension: tensionValue,
  backgroundColor: ["rgba(247, 89, 171, 1)"],
  borderColor: ["rgba(247, 89, 171, 1)"],
  borderWidth: 2,
};

const state = {
  backend: "wasm",
};

async function setupCamera() {
  webcam = document.getElementById("webcam");

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    // 'video': {facingMode: 'user'},
    video: { width: 520, height: 310 },
  });
  webcam.srcObject = stream;

  return new Promise((resolve) => {
    webcam.onloadedmetadata = () => {
      resolve(webcam);
    };
  });
}

function emotionValue(emotion, toFixed = 2) {
  return emotion.toFixed(toFixed);
}

function emotionValuePercent(emotion, toFixed = 2) {
  return Math.round((emotion * 100).toFixed(toFixed));
}

function displayEmotionValues(emotions) {
  valueNeutral.style.width = `${emotionValuePercent(emotions[0])}%`;
  valueNeutralLabel.textContent = `${emotionValuePercent(emotions[0])}%`;
  valueHappy.style.width = `${emotionValuePercent(emotions[1])}%`;
  valueHappyLabel.textContent = `${emotionValuePercent(emotions[1])}%`;
  valueSad.style.width = `${emotionValuePercent(emotions[2])}%`;
  valueSadLabel.textContent = `${emotionValuePercent(emotions[2])}%`;
  valueAngry.style.width = `${emotionValuePercent(emotions[3])}%`;
  valueAngryLabel.textContent = `${emotionValuePercent(emotions[3])}%`;
  valueSuprised.style.width = `${emotionValuePercent(emotions[4])}%`;
  valueSuprisedLabel.textContent = `${emotionValuePercent(emotions[4])}%`;

  valuePositive.style.width = `${emotionValuePercent(
    pipeline.estimatePositive(emotions)
  )}%`;
  valuePositiveLabel.textContent = `${emotionValuePercent(
    pipeline.estimatePositive(emotions)
  )}%`;
  valueActive.style.width = `${emotionValuePercent(
    pipeline.estimateActive(emotions)
  )}%`;
  valueActiveLabel.textContent = `${emotionValuePercent(
    pipeline.estimateActive(emotions)
  )}%`;
  valueVibe.style.width = `${emotionValuePercent(
    pipeline.estimateVibe(emotions)
  )}%`;
  valueVibeLabel.textContent = `${emotionValuePercent(
    pipeline.estimateVibe(emotions)
  )}%`;
}

function displayBoundingBox(
  face,
  newWidth,
  newHeight,
  oldWidth,
  oldHeight,
  returnTensors = false
) {
  if (returnTensors) {
    face.topLeft = face.topLeft.arraySync();
    face.bottomRight = face.bottomRight.arraySync();
    if (annotateBoxes) {
      face.landmarks = face.landmarks.arraySync();
    }
  }

  const start = face.topLeft;
  const end = face.bottomRight;
  const size = [end[0] - start[0], end[1] - start[1]];

  ctxOutput.strokeStyle = "rgba(0, 255, 0, 1.0)";
  const ratioWidth = newWidth / oldWidth;
  const ratioHeight = newHeight / oldHeight;

  const newX = convertRange(start[0], newWidth, oldWidth);
  const newY = convertRange(start[1], newHeight, oldHeight);

  ctxOutput.strokeRect(newX, newY, size[0] * ratioWidth, size[1] * ratioHeight);
}

function pushEmotionsToChart(emotions) {
  const emotionsNeutral = [emotionValue(emotions[0] * 100, 3)];
  dataEmotionsNeutral.push(emotionsNeutral);

  const emotionsHappy = [emotionValue(emotions[1] * 100, 3)];
  dataEmotionsHappy.push(emotionsHappy);

  const emotionsSad = [emotionValue(emotions[2] * 100, 3)];
  dataEmotionsSad.push(emotionsSad);

  const emotionsAngry = [emotionValue(emotions[3] * 100, 3)];
  dataEmotionsAngry.push(emotionsAngry);

  const emotionsSurprise = [emotionValue(emotions[4] * 100, 3)];
  dataEmotionsSurprised.push(emotionsSurprise);

  const emotionPositive = [
    emotionValue(pipeline.estimatePositive(emotions) * 100, 3),
  ];
  dataEmotionsPositive.push(emotionPositive);

  const emotionActive = [
    emotionValue(pipeline.estimateActive(emotions) * 100, 3),
  ];
  dataEmotionsActive.push(emotionActive);

  const emotionVibe = [emotionValue(pipeline.estimateVibe(emotions) * 100, 3)];
  dataEmotionsVibe.push(emotionVibe);

  if (dataEmotionsAngry.length >= maxFrameChart) {
    numberCell += 1;
    numberCells.push(numberCell);
  }

  myChart.update();
  myChart.data.labels = numberCells;
  myChart.data.datasets[0].data = dataEmotionsAngry;
  myChart.data.datasets[1].data = dataEmotionsNeutral;
  myChart.data.datasets[2].data = dataEmotionsHappy;
  myChart.data.datasets[3].data = dataEmotionsSad;
  myChart.data.datasets[4].data = dataEmotionsSurprised;
  myChart.data.datasets[5].data = dataEmotionsPositive;
  myChart.data.datasets[6].data = dataEmotionsActive;
  myChart.data.datasets[7].data = dataEmotionsVibe;

  if (dataEmotionsAngry.length >= maxFrameChart + 1) {
    dataEmotionsAngry.shift();
    dataEmotionsNeutral.shift();
    dataEmotionsHappy.shift();
    dataEmotionsSad.shift();
    dataEmotionsSurprised.shift();
    dataEmotionsPositive.shift();
    dataEmotionsActive.shift();
    dataEmotionsVibe.shift();
    numberCells.shift();
    myChart.update();
  }
}

function convertRange(value, newMax, oldMax) {
  // const oldMax = oldRange;
  const oldMin = 0;
  // const newMax = newRange;
  const newMin = 0;
  const oldRange = oldMax - oldMin;
  const newRange = newMax - newMin;
  const newValue = ((value - oldMin) * newRange) / oldRange + newMin;
  return newValue;
}

const renderPrediction = async () => {
  stats.begin();

  let time = Date.now();

  if (time >= prevTime + timeFrameCapture) {
    prevTime = time;
    let viewer;
    // eslint-disable-next-line one-var
    let oldWidth, oldHeight, newWidth, newHeight;
    let totalPredictions = 0;
    let emotionsNeutral = [];
    let emotionsHappy = [];
    let emotionsSad = [];
    let emotionsAngry = [];
    let emotionsSurprise = [];
    let face;
    let emotions;
    // const checkImgEmpty = document.getElementById('img');
    // const src = checkImgEmpty.getAttribute('src');
    if (viewerType == "img") {
      viewer = img;
      canvas.width = img.offsetWidth;
      canvas.height = img.offsetHeight;
      oldWidth = img.width;
      oldHeight = img.height;
      newWidth = img.offsetWidth;
      newHeight = img.offsetHeight;
    } else if (viewerType == "video") {
      oldWidth = videoClip.videoWidth;
      oldHeight = videoClip.videoHeight;
      newWidth = videoClip.offsetWidth;
      newHeight = videoClip.offsetHeight;
      canvas.width = videoClip.offsetWidth;
      canvas.height = videoClip.offsetHeight;
      // scale the canvas accordingly
      canvasV.width = videoClip.videoWidth;
      canvasV.height = videoClip.videoHeight;
      // draw the video at that frame
      canvasV
        .getContext("2d")
        .drawImage(videoClip, 0, 0, canvasV.width, canvasV.height);
      viewer = canvasV;
    } else if (viewerType === "webcam") {
      viewer = webcam;
      oldWidth = webcam.videoWidth;
      oldHeight = webcam.videoHeight;
      newWidth = webcam.offsetWidth;
      newHeight = webcam.offsetHeight;
      canvas.width = viewer.offsetWidth;
      canvas.height = viewer.offsetHeight;
    } else {
      return;
    }

    if (oldWidth > 0 && oldHeight > 0) {
      const predictions = await pipeline.estimateEmotion(viewer);
      const predictionsMask = await pipeline.estimateMask(viewer);
      // console.log(predictionsMask);
      ctxOutput.clearRect(0, 0, canvas.width, canvas.height);

      if (predictions.length > 0) {
        for (let i = 0; i < predictions.length; i++) {
          face = predictions[i].face;
          emotions = predictions[i].emotions;
          if (face.probability >= 0.5) {
            totalPredictions += 1;
            emotionsNeutral.push(emotions[0]);
            emotionsHappy.push(emotions[1]);
            emotionsSad.push(emotions[2]);
            emotionsAngry.push(emotions[3]);
            emotionsSurprise.push(emotions[4]);
            displayBoundingBox(face, newWidth, newHeight, oldWidth, oldHeight);
          }
        }

        if (totalPredictions > 0) {
          let wearMask = false;
          for (let i = 0; i < predictionsMask.length; i++) {
            const mask = predictionsMask[i].mask;
            if (mask != undefined && mask[0] > 0.8) {
              wearMask = true;
            }
          }

          if (wearMask) {
            document.getElementById("wear_mask").classList.remove("hidden");
          } else {
            document.getElementById("wear_mask").classList.add("hidden");
          }

          const avgNeutral =
            emotionsNeutral.reduce((a, b) => a + b, 0) / totalPredictions;
          const avgHappy =
            emotionsHappy.reduce((a, b) => a + b, 0) / totalPredictions;
          const avgSad =
            emotionsSad.reduce((a, b) => a + b, 0) / totalPredictions;
          const avgAngry =
            emotionsAngry.reduce((a, b) => a + b, 0) / totalPredictions;
          const avgSurprise =
            emotionsSurprise.reduce((a, b) => a + b, 0) / totalPredictions;

          avgEmotions = [avgNeutral, avgHappy, avgSad, avgAngry, avgSurprise];
          pushEmotionsToChart(avgEmotions);
          displayEmotionValues(avgEmotions);
        }
      }
    }
  }
  stats.end();
  requestAnimationFrame(renderPrediction);
};

async function showEmotions() {
  myChart.getDatasetMeta(0).hidden = false;
  myChart.getDatasetMeta(1).hidden = false;
  myChart.getDatasetMeta(2).hidden = false;
  myChart.getDatasetMeta(3).hidden = false;
  myChart.getDatasetMeta(4).hidden = false;
}

async function showPositiveActive() {
  myChart.getDatasetMeta(5).hidden = false;
  myChart.getDatasetMeta(6).hidden = false;
}

async function showVibes() {
  myChart.getDatasetMeta(7).hidden = false;
}

const setupMyChartEmotions = async () => {
  numberCell = maxFrameChart;
  numberCells = Array.from({ length: maxFrameChart }, (_, i) => i + 1);
  dataEmotionsNeutral = Array.from({ length: maxFrameChart }, (_, i) => 0);
  dataEmotionsAngry = Array.from({ length: maxFrameChart }, (_, i) => 0);
  dataEmotionsHappy = Array.from({ length: maxFrameChart }, (_, i) => 0);
  dataEmotionsSad = Array.from({ length: maxFrameChart }, (_, i) => 0);
  dataEmotionsSurprised = Array.from({ length: maxFrameChart }, (_, i) => 0);
  dataEmotionsPositive = Array.from({ length: maxFrameChart }, (_, i) => 0);
  dataEmotionsActive = Array.from({ length: maxFrameChart }, (_, i) => 0);
  dataEmotionsVibe = Array.from({ length: maxFrameChart }, (_, i) => 0);
  const ctxChart = document.getElementById("myChart").getContext("2d");
  myChart = new Chart(ctxChart, {
    backgroundColor: "white",
    type: "line",
    data: {
      labels: numberCells,
      datasets: [
        datasetAngry,
        datasetNeutral,
        datasetHappy,
        datasetSad,
        datasetSurprised,
        datasetPositive,
        datasetActive,
        datasetVibe,
      ],
    },
    options: {
      maintainAspectRatio: false,
      animations: {
        duration: timeFrameCapture * 1.5,
        easing: "linear",
      },
      scales: {
        yAxes: [
          {
            ticks: {
              stepSize: 10,
              beginAtZero: true,
              min: 0,
              max: 100,
            },
          },
        ],
        xAxes: [
          {
            gridLines: {
              display: false,
            },
            position: "bottom",
            ticks: {
              beginAtZero: true,
              display: false,
            },
          },
        ],
      },
      legend: {
        display: false,
      },
      tooltips: {
        enabled: false,
      },
      hover: {
        mode: null,
      },
      elements: {
        point: {
          radius: 0,
        },
      },
    },
  });
};

function hideDataEmotions() {
  myChart.getDatasetMeta(0).hidden = true;
  myChart.getDatasetMeta(1).hidden = true;
  myChart.getDatasetMeta(2).hidden = true;
  myChart.getDatasetMeta(3).hidden = true;
  myChart.getDatasetMeta(4).hidden = true;
}

function hideDataPositive() {
  myChart.getDatasetMeta(5).hidden = true;
  myChart.getDatasetMeta(6).hidden = true;
}

function hideDataVibe() {
  myChart.getDatasetMeta(7).hidden = true;
}

const setupModel = async () => {
  // const [loadedFace, loadedEmotion, loadedMask] = await Promise.all([
  //   facetfjs.loadBlazeFace(),
  //   facetfjs.loadFaceEmotions(),
  //   facetfjs.loadMask(),
  // ]);

  // modelFace = loadedFace;
  // modelEmotion = loadedEmotion;
  // modelMask = loadedMask;
  modelFace = await facetfjs.loadBlazeFace();
  modelEmotion = await facetfjs.loadFaceEmotions();
  modelMask = await facetfjs.loadMask();

  pipeline = new facetfjs.Pipeline(modelFace, modelEmotion, modelMask);
};

function setupEmotionElements() {
  valueNeutral = document.getElementById("value_neutral");
  valueHappy = document.getElementById("value_happy");
  valueSad = document.getElementById("value_sad");
  valueAngry = document.getElementById("value_angry");
  valueSuprised = document.getElementById("value_suprised");

  valuePositive = document.getElementById("value_positive");
  valueActive = document.getElementById("value_active");
  valueVibe = document.getElementById("value_vibe");

  valueNeutralLabel = document.getElementById("value_neutral_label");
  valueHappyLabel = document.getElementById("value_happy_label");
  valueSadLabel = document.getElementById("value_sad_label");
  valueAngryLabel = document.getElementById("value_angry_label");
  valueSuprisedLabel = document.getElementById("value_suprised_label");

  valuePositiveLabel = document.getElementById("value_positive_label");
  valueActiveLabel = document.getElementById("value_active_label");
  valueVibeLabel = document.getElementById("value_vibe_label");
}

function rememberNoPopup() {
  localStorage.setItem("remember", true);
}

function checkEmotion(e) {
  const statusChecked = e.target.checked;

  if (statusChecked) {
    showEmotions();
  } else {
    hideDataEmotions();
  }
}

function checkActivePositive(e) {
  const statusChecked = e.target.checked;

  if (statusChecked) {
    showPositiveActive();
  } else {
    hideDataPositive();
  }
}

function checkVibes(e) {
  const statusChecked = e.target.checked;

  if (statusChecked) {
    showVibes();
  } else {
    hideDataVibe();
  }
}

function scrollBottom() {
  document
    .getElementById("myChart")
    .scrollIntoView({ block: "nearest", inline: "nearest" });
}
const startTime = Date.now();

const setupPage = async () => {
  await tf.setBackend(state.backend);

  document
    .getElementById("remember_no_popup")
    .addEventListener("click", rememberNoPopup);

  const remember = localStorage.getItem("remember");
  if (remember !== undefined && remember) {
  } else {
    toggleModal("authorized_camera");
  }

  document.getElementById("section_content").scrollTop = 0;
  document.getElementById("btn_scroll_down").classList.remove("hidden");

  // Promise.all([setupModel(), setupCamera()]).then(()=>{
  // webcam.play();
  // console.log('done');
  // }).catch((error)=>{
  //   console.log(error);
  // });

  await setupModel().then(() => {
    const endTime = Date.now();
    const elapsedTime = endTime - startTime;
    console.log(`Time load model taken about: ${elapsedTime} milliseconds`);
  });

  await setupCamera().then(() => {
    const endTime = Date.now();
    const elapsedTime = endTime - startTime;
    console.log(`Time load camera taken about: ${elapsedTime} milliseconds`);
  });
  webcam.play();

  viewerType = "webcam";

  videoWidth = webcam.videoWidth;
  videoHeight = webcam.videoHeight;
  webcam.width = videoWidth;
  webcam.height = videoHeight;

  canvasV = document.createElement("canvas");
  // imgVideo = document.createElement('img');

  canvas = document.getElementById("output");
  canvas.width = videoWidth;
  canvas.height = videoHeight;
  ctxOutput = canvas.getContext("2d");
  ctxOutput.fillStyle = "rgba(255, 0, 0, 0.5)";

  setupEmotionElements();
  setupMyChartEmotions();
  hideDataPositive();
  hideDataVibe();
  renderPrediction();

  document.getElementById("load_indicator").classList.add("hidden");
  document.getElementById("webcam").classList.remove("hidden");
  document.getElementById("output").classList.remove("hidden");

  document
    .getElementById("checkEmotion")
    .addEventListener("change", checkEmotion);
  document
    .getElementById("checkActivePositive")
    .addEventListener("change", checkActivePositive);
  document.getElementById("checkVibes").addEventListener("change", checkVibes);

  const btnScrollToBottom = document.getElementById("btnScrollToBottom");

  const sectionContent = document.getElementById("section_content");

  sectionContent.addEventListener("scroll", () => {
    if (sectionContent.scrollTop > 0) {
      btnScrollToBottom.classList.add("!hidden");
    } else {
      btnScrollToBottom.classList.remove("!hidden");
    }
  });

  btnScrollToBottom.addEventListener("click", scrollBottom);
  btnScrollToBottom.addEventListener("touchstart", scrollBottom);
};

setupPage();
